import { ROUTE_MAP } from "../constants/routes";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import { translatePath } from "./routeHelpers";

/**
 * [OBSZAR B 2026-09-03, poz. 3c/3d] DOKĄD PROWADZI POWIADOMIENIE.
 *
 * Zgłoszenie właściciela: „jak użyję powiadomienia z centrum informacji czy widgeta, ZRÓB MI LINK,
 * żeby przeniosło mnie do miejsca powiadomienia, żeby dało się realnie nawigować".
 *
 * ─── Co było źle ─────────────────────────────────────────────────────────────
 * Dzwonek i centrum robiły dokładnie to samo: `if (notification.link) navigate(...)`. Wynikały
 * z tego DWIE ciche usterki:
 *  1. link ZAPISANY w bazie, którego nie ma w mapie tras, kończył przeniesieniem na pulpit
 *     (105 takich powiadomień u 13 osób — patrz LinkiPowiadomienIstniejaWMapieTrasTests);
 *  2. powiadomienie BEZ linku wyglądało na klikalne (kursor „łapka", `role="button"`), a klik
 *     nie robił NIC. Człowiek klikał trzy razy i uznawał, że aplikacja się zawiesiła.
 *
 * ─── Co robi ten moduł ───────────────────────────────────────────────────────
 * Jedno miejsce liczy adres docelowy dla OBU wejść (dzwonek + centrum):
 *  • jest link → prowadzimy pod link (przetłumaczony na język interfejsu);
 *  • nie ma linku → ZAPAS wg kodu typu, z mapy niżej. To ma być decyzja, nie przypadek:
 *    mapa wymienia tylko te rodziny, dla których istnieje JEDNA oczywista lista;
 *  • nie ma linku i nie ma zapasu → zwracamy `null`, a interfejs pokazuje wiersz jako
 *    NIEKLIKALNY (bez „łapki", bez `role="button"`), z podpowiedzią dlaczego.
 *
 * Nigdy nie odsyłamy na pulpit „bo coś trzeba zrobić" — pulpit jako cel to właśnie ten ślepy
 * zaułek, na który skarżył się właściciel.
 */

/**
 * ZAPAS: kod typu powiadomienia → klucz trasy z `routes.js`.
 *
 * Reguła doboru: wpisujemy tylko wtedy, gdy dla całej rodziny istnieje jedna lista, na której
 * człowiek NA PEWNO znajdzie sprawę z powiadomienia. Tam, gdzie takiej listy nie ma (życzenia
 * urodzinowe, komunikat techniczny, zmiana danych w stopce e-mail — odbiorcy z puli kadr nie
 * mają wejścia do panelu stopek), świadomie NIE MA wpisu: lepszy uczciwy brak celu niż odesłanie
 * na ekran „Brak dostępu".
 */
export const ZAPASOWE_CELE_POWIADOMIEN = {
    // Zgłoszenia — „Moje zgłoszenia" ma każdy zalogowany; kolejka obsługi już nie.
    ticket_assigned: "myTickets",
    ticket_status_changed: "myTickets",
    ticket_new_reply: "myTickets",
    ticket_new_note: "myTickets",

    // Zadania i Kanban.
    task_assigned: "tasks",
    task_shared: "tasks",
    task_unshared: "tasks",
    task_completed: "tasks",
    kanban_task_assigned: "kanban",
    kanban_task_due: "kanban",
    kanban_task_comment: "kanban",
    kanban_task_updated: "kanban",
    kanban_board_shared: "kanban",
    kanban_member_removed: "kanban",

    // Treści.
    news_published: "news",
    gallery_published: "gallery",
    contest_started: "contests",
    contest_results: "contests",
    contest_submission_received: "contests",
    contest_jury_assigned: "jury",
    document_published: "documents",
    kb_article_published: "knowledgeBase",
    faq_published: "knowledgeBase",
    survey_available: "surveys",
    elearning_published: "eLearning",
    elearning_updated: "eLearning",
    announcement_new: "notificationCenter",

    elearning_enrolled: "eLearning",
    elearning_deadline: "eLearning",
    elearning_completed: "eLearning",
    job_offer_published: "jobOffers",
    job_application_status: "jobOffers",
    job_referral_status: "jobOffers",

    // Sprawy pracownika.
    hr_request_status: "hrRequests",
    hr_request_assigned: "hrFullView",
    hr_pending_approval: "myDeptRequests",
    hr_pending_approval_kadry: "hrFullView",
    hr_low_leave_balance: "myDeptLeave",
    hrdoc_issued: "hrMyDocuments",
    hrdoc_signed: "hrMyDocuments",
    goal_assigned: "myGoals",
    goal_accepted: "myGoals",
    goal_rejected: "myGoals",
    goal_due_soon: "myGoals",
    goal_overdue: "myGoals",
    goal_evaluated: "myGoals",
    goal_completed: "myGoals",
    onboarding_status: "onboarding",
    registration_approved: "registrationRequests",
    registration_correction: "registrationRequests",
    registration_submitted: "registrationRequests",
    drive_access_granted: "myFiles",
    drive_access_requested: "myFiles",
    contact_form_received: "contactForm",

    // Projekty (Listy projektowe).
    pl_project_added: "projects",
    pl_item_assigned: "projects",
    pl_item_due: "projects",
    pl_item_unblocked: "projects",
    pl_access_decided: "projects",
};

/**
 * Adres docelowy powiadomienia albo `null`, gdy świadomie nie ma dokąd prowadzić.
 * Zwracany adres jest już w języku interfejsu — nadaje się wprost do `navigate(...)`.
 */
export function celPowiadomienia(notification) {
    if (!notification) return null;

    const link = (notification.link || "").trim();
    if (link) return translatePath(link);

    const klucz = ZAPASOWE_CELE_POWIADOMIEN[notification.typeCode];
    if (klucz && ROUTE_MAP[klucz]) return localizedPath(klucz);

    return null;
}

/** Podpowiedź pod kursor dla wiersza bez celu — żeby brak reakcji na klik nie wyglądał na awarię. */
export const BRAK_CELU_PODPOWIEDZ =
    "To powiadomienie jest wyłącznie informacją — nie ma osobnej strony, na którą mogłoby przenieść.";
